import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { SupportedLocale } from 'i18n/supportedLocales'

interface Props {
  authLocale: SupportedLocale
  urlLocale: SupportedLocale
}

type ConflictChoice = 'url' | 'auth'

function LocaleRedirectModal({ authLocale, urlLocale }: Props) {
  const intl = useIntl()
  const modalContext = useModalElementContext<ConflictChoice>()
  const onClose = useCallback(() => {
    modalContext.resolve('url')
  }, [modalContext])

  const title = intl.formatMessage({
    id: 'jVH8voIfhVh0RZcbF60E',
    defaultMessage: 'Choose your language',
    description: 'Title for a modal asking the customer to resolve a URL language and account language conflict',
  })
  const urlLanguageLabel = intl.formatDisplayName(urlLocale, { type: 'language' }) ?? urlLocale
  const authLanguageLabel = intl.formatDisplayName(authLocale, { type: 'language' }) ?? authLocale

  return <Modal
    title={title}
    primaryActionText={intl.formatMessage({
      id: 'IxcWPnlHJk6iU26DOndj',
      defaultMessage: 'Use account language',
      description: 'Primary action to switch the page to the language stored on the account',
    })}
    secondaryActionText={intl.formatMessage({
      id: 'mvniesvnqwYRAJMKgWqJ',
      defaultMessage: 'Keep page language',
      description: 'Secondary action to keep the language currently present in the URL',
    })}
    onPrimaryActionClick={(_event, resolve) => resolve('auth')}
    onSecondaryActionClick={(_event, resolve) => resolve('url')}
    onClose={onClose}
  >
    <BodyText variant="large">
      {intl.formatMessage({
        id: '4Z4zmNyyNhN2PC0qkMZ9',
        defaultMessage: 'This page is in {urlLanguage}, but your account language is {authLanguage}.',
        description: 'Body text explaining a URL language and account language conflict',
      }, {
        urlLanguage: urlLanguageLabel,
        authLanguage: authLanguageLabel,
      })}
    </BodyText>
  </Modal>
}

export default LocaleRedirectModal
